<template>
  <v-row no-gutters justify="space-between" class="flex-column flex-md-row my-n2">
    <v-col class="pr-xxl-2 my-2 input_holder col-12 col-xxl-6">
      <label>{{ $t('profile.multifactor.label') }}</label>

      <v-radio-group
        v-model="twoFactorAuthorizationTypeId"
        :disabled="disabled"
        class="mfa-group mt-2"
      >
        <div
          v-if="!isTwoFactorRequired"
          class="mfa-option"
          :class="{ active: twoFactorAuthorizationTypeId === 0 }"
        >
          <v-radio
            :value="0"
            :label="$t('profile.multifactor.off')"
            :disabled="disabled"
            hide-details
            dense
          />
          <div v-if="twoFactorAuthorizationTypeId === 0" class="helper">
            {{ $t('profile.multifactor.offInfo') }}
          </div>
        </div>

        <div
          v-if="twoFactorAuthorizationTypeId === 1"
          class="mfa-option"
          :class="{ active: twoFactorAuthorizationTypeId === 1 }"
        >
          <div class="d-flex align-center">
            <v-radio
              :value="1"
              :label="$t('profile.multifactor.sms')"
              :disabled="!hasPhone || disabled"
              hide-details
              dense
            />
            <span v-if="!hasPhone" class="ml-2 text-caption text-muted">
              {{ $t('profile.multifactor.noPhone') }}
            </span>
          </div>
          <div v-if="twoFactorAuthorizationTypeId === 1" class="helper">
            {{ $t('profile.multifactor.smsInfo') }}
          </div>
        </div>

        <div class="mfa-option" :class="{ active: twoFactorAuthorizationTypeId === 2 }">
          <v-radio
            :value="2"
            :label="$t('profile.multifactor.email')"
            :disabled="disabled"
            hide-details
            dense
          />
          <div v-if="twoFactorAuthorizationTypeId === 2" class="helper">
            {{ $t('profile.multifactor.emailInfo') }}
          </div>
        </div>
      </v-radio-group>
    </v-col>
  </v-row>
</template>

<script>
import { multifactor as multifactorModule, MFA_TYPES } from '@/store/forms/multifactor';

export default {
  name: 'Multifactor',
  props: {
    disabled: { type: Boolean, default: false }
  },
  computed: {
    twoFactorAuthorizationTypeId: {
      get() {
        const storeValue = this.$store.state.forms?.multifactor?.twoFactorAuthorizationTypeId;
        return storeValue === null ? MFA_TYPES.OFF : Number(storeValue);
      },
      set(selectedValue) {
        const numericValue = Number(selectedValue);

        // Don't allow selecting SMS when there's no phone
        if (numericValue === MFA_TYPES.SMS && !this.hasPhone) {
          return;
        }

        // Don't allow selecting OFF when 2FA is required for clinic access
        if (numericValue === MFA_TYPES.OFF && this.isTwoFactorRequired) {
          return;
        }

        if (!this.$store.hasModule(['forms', 'multifactor'])) {
          return;
        }

        this.$store.commit('forms/multifactor/setTwoFactorAuthorizationTypeId', numericValue);
        this.$emit('change', numericValue);
        this.$emit('input', numericValue);
      }
    },
    hasPhone() {
      return !!this.$auth?.user?.phone && this.$auth.user.verify.phone;
    },
    isTwoFactorRequired() {
      // If backend doesn't have hasAllowAccessToClinicsApp field yet, behave as false (2FA not required)
      return this.$auth?.user?.hasAllowAccessToClinicsApp === true;
    }
  },
  created() {
    if (!this.$store.hasModule('forms')) {
      this.$store.registerModule('forms', { namespaced: true });
    }

    if (!this.$store.hasModule(['forms', 'multifactor'])) {
      this.$store.registerModule(['forms', 'multifactor'], multifactorModule);
    }

    const userValue = this.$auth?.user?.twoFactorAuthorizationTypeId;
    this.$store.dispatch('forms/multifactor/hydrateFromAuth', {
      twoFactorAuthorizationTypeId: userValue
    });

    const currentValue = this.$store.state.forms.multifactor.twoFactorAuthorizationTypeId;

    // If SMS is set but no phone, turn off 2FA
    if (currentValue === MFA_TYPES.SMS && !this.hasPhone) {
      this.$store.commit('forms/multifactor/setTwoFactorAuthorizationTypeId', MFA_TYPES.OFF);
    }

    // If 2FA is required for clinic access but currently off, set to email
    if (this.isTwoFactorRequired && currentValue === MFA_TYPES.OFF) {
      this.$store.commit('forms/multifactor/setTwoFactorAuthorizationTypeId', MFA_TYPES.EMAIL);
    }
  }
};
</script>

<style scoped>
.mfa-group {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px 24px;
}
.mfa-group .v-input__control {
  display: contents;
}
.mfa-option {
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 6px;
}
.helper {
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 2px;
}
</style>
